import { useEffect, useState } from 'react';
import { driverAPI, userAPI, lookupAPI, API_BASE } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, StatusBadge, Modal, Input, Select, Loading, Alert } from '../components/ui';

export default function Drivers() {
  const { can } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [users,   setUsers]   = useState([]);
  const [licTypes, setLicTypes] = useState(['Light Vehicle','Heavy Vehicle']);
  const [lic, setLic] = useState({});   // { 'Light Vehicle': { on, no, exp }, ... }

  const setLicField = (type, patch) =>
    setLic(prev => ({ ...prev, [type]: { ...(prev[type] || {}), ...patch } }));
  const licArray = () => Object.entries(lic)
    .filter(([, v]) => v?.on)
    .map(([type, v]) => ({ license_type: type, license_no: v.no || '', expiry_date: v.exp || '' }));
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [search,  setSearch]  = useState('');
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ user_id:'', license_number:'', license_type:'Light Vehicle', license_expiry:'', dob:'', address:'', emergency_contact:'' });
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([driverAPI.getAll({ search, limit: 50 }), userAPI.getAll().catch(() => ({ data: { data: [] } }))])
      .then(([d, u]) => { setDrivers(d.data.data); setUsers(u.data.data.filter(x => x.role === 'driver')); })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [search]);
  useEffect(() => {
    lookupAPI.getAll({ category: 'license_type' })
      .then(r => { const l = r.data.data?.license_type; if (l?.length) setLicTypes(l); })
      .catch(() => {});
  }, []);

  const openAdd  = () => {
    setEditing(null); setLic({});
    setForm({ user_id:'', license_number:'', license_type:'Light Vehicle', license_expiry:'', dob:'', address:'', emergency_contact:'', alt_mobile:'', status:'active' });
    setModal(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ ...d, user_id: d.user_id || '' });
    const map = {};
    (d.licenses || []).forEach(l => {
      map[l.license_type] = { on: true, no: l.license_no || '', exp: l.expiry_date || '' };
    });
    // fall back to the legacy single-licence fields
    if (!Object.keys(map).length && d.license_number) {
      map[d.license_type || 'Light Vehicle'] = { on: true, no: d.license_number, exp: d.license_expiry || '' };
    }
    setLic(map);
    setModal(true);
  };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const licenses = licArray();
      if (!licenses.length) { setError('Add at least one licence type'); setSaving(false); return; }
      const payload = { ...form, licenses };
      editing ? await driverAPI.update(editing.id, payload) : await driverAPI.create(payload);
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Drivers" sub={`${drivers.length} registered drivers`}
        action={can('admin','transport_officer') && <Button onClick={openAdd}>+ Add Driver</Button>} />
      {error && <Alert message={error} className="mb-4" />}
      <div className="mb-5">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drivers…"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-64 outline-none focus:ring-2 focus:ring-primary-300" />
      </div>
      {loading ? <Loading /> : (
        <Table headers={['Name','Licences','Expiry','Status','Actions']}>
          {drivers.map(d => (
            <tr key={d.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {d.photo_path
                    ? <img src={`${API_BASE}${d.photo_path}?t=${Date.parse(d.updated_at || 0) || ''}`} alt=""
                        className="w-9 h-9 rounded-full object-cover border" />
                    : <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                        {(d.user?.name || '?').charAt(0)}
                      </div>}
                  <div>
                    <p className="font-medium text-sm">{d.user?.name || '—'}</p>
                    <p className="text-xs text-gray-400">{d.user?.email}</p>
                    <p className="text-xs text-gray-400">
                      {d.user?.phone || ''}{d.user?.phone && d.alt_mobile ? ' / ' : ''}{d.alt_mobile || ''}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm">
                {(d.licenses?.length ? d.licenses : [{ license_type: d.license_type, license_no: d.license_number, expiry_date: d.license_expiry }])
                  .filter(l => l.license_type || l.license_no)
                  .map((l, i) => (
                    <div key={i} className="mb-0.5 last:mb-0">
                      <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 text-xs">{l.license_type}</span>
                      <span className="ml-2 font-mono text-xs text-gray-600">{l.license_no || '—'}</span>
                    </div>
                  ))}
              </td>
              <td className="px-4 py-3 text-sm">
                {(d.licenses?.length ? d.licenses : [{ expiry_date: d.license_expiry }])
                  .filter(l => l.expiry_date)
                  .map((l, i) => (
                    <p key={i} className={new Date(l.expiry_date) < new Date(Date.now()+30*86400000) ? 'text-amber-600 font-medium text-xs' : 'text-gray-600 text-xs'}>
                      {l.expiry_date}
                    </p>
                  ))}
              </td>
              <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
              <td className="px-4 py-3">
                {can('admin','transport_officer') && <Button variant="ghost" size="sm" onClick={() => openEdit(d)}>Edit</Button>}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Driver' : 'Add Driver'}>
        {editing && (
          <div className="flex items-center gap-4 mb-4 pb-4 border-b">
            {editing.photo_path
              ? <img src={`${API_BASE}${editing.photo_path}?t=${Date.now()}`} alt=""
                  className="w-16 h-16 rounded-full object-cover border" />
              : <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-semibold">
                  {(editing.user?.name || '?').charAt(0)}
                </div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Driver Photo</label>
              <input type="file" accept="image/jpeg,image/png,image/webp"
                className="text-sm"
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const r = await driverAPI.uploadPhoto(editing.id, file);
                    setEditing(r.data.data);
                    load();
                  } catch { setError('Photo upload failed'); }
                }} />
              <p className="text-xs text-gray-400 mt-1">JPG / PNG / WEBP, max 10MB. Replaces the previous photo.</p>
            </div>
          </div>
        )}
        <form onSubmit={save} className="space-y-4">
          <Select label="User Account" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})}>
            <option value="">Select user…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </Select>
          {form.user_id && (() => {
            const u = users.find(x => String(x.id) === String(form.user_id));
            return (
              <p className="-mt-2 text-xs text-gray-500">
                Mobile No: <span className="font-medium text-gray-700">{u?.phone || 'not set on user account'}</span>
              </p>
            );
          })()}
          {/* Licences — a driver may hold more than one type */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Licences</p>
              <p className="text-xs text-gray-400">Tick each licence the driver holds and enter its own number and expiry</p>
            </div>
            <div className="divide-y divide-gray-100">
              {licTypes.map(t => {
                const v = lic[t] || {};
                return (
                  <div key={t} className={v.on ? 'bg-primary-50/40' : ''}>
                    <label className="flex items-center gap-2 px-4 py-2.5 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4" checked={!!v.on}
                        onChange={e => setLicField(t, { on: e.target.checked })} />
                      <span className="text-sm font-medium">{t}</span>
                    </label>
                    {v.on && (
                      <div className="grid grid-cols-2 gap-3 px-4 pb-3">
                        <Input label="Licence No." value={v.no || ''}
                          onChange={e => setLicField(t, { no: e.target.value })} placeholder="e.g. B1234567" />
                        <Input label="Expiry Date" type="date" value={v.exp || ''}
                          onChange={e => setLicField(t, { exp: e.target.value })} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Input label="Date of Birth" type="date" value={form.dob} onChange={e => setForm({...form, dob: e.target.value})} />
          <Input label="Emergency Contact" value={form.emergency_contact} onChange={e => setForm({...form, emergency_contact: e.target.value})} />
          <Input label="Alternative Mobile No" value={form.alt_mobile || ''} onChange={e => setForm({...form, alt_mobile: e.target.value})} placeholder="07X XXXXXXX" />
          <Select label="Status" value={form.status || 'active'}
            onChange={e => setForm({...form, status: e.target.value})}>
            <option value="active">Active</option>
            <option value="suspended">Suspend</option>
            <option value="resigned">Resigned</option>
          </Select>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
